import { NgModule, Component, Input, Output, EventEmitter, ViewChild, ChangeDetectorRef, AfterViewInit, TemplateRef, OnInit, ViewEncapsulation } from '@angular/core';
import { TreeNode, RowNode, TreeTableComponent } from '@farris/ui-treetable';
import { CommonUtils } from '@farris/ui-common';
import { FarrisCommonModule } from '@farris/ui-common';
import { TreeTableModule } from '@farris/ui-treetable';
import { ShowTitleDirective } from './show-title.directive';

/** 节点id的前缀 */
export const TREE_ID_PREFIX = 'TreeRow-';
/** 节点内容的指定字段 */
export const NODE_CONTENT_FIELD = 'CodeViewNav--ContentInnerHTML';

@Component({
  selector: 'nav-tree',
  templateUrl: './tree.html',
  host: {
    "[style.display]": "'block'",
    "[style.height]": "'100%'",
    "[class.tree-expendIcon--chevron]": "expendIcon === 'chevron'",
    "[class.tree-expendIcon--triangle]": "expendIcon === 'triangle'"
  },
  styles: [
    'nav-tree .treetable-cell-content .text { overflow: hidden}'
  ],
  encapsulation: ViewEncapsulation.None
})
export class TreeComponent implements OnInit, AfterViewInit {

  @ViewChild(TreeTableComponent) treeTable: TreeTableComponent;

  @ViewChild('treeNode') treeNodeTemplate: TemplateRef<any>;

  /** 收起展开图标的类型 */
  @Input() expendIcon: 'default' | 'chevron' | 'triangle';

  @Input() showTips = true;

  @Input() width = 500;

  @Input() height = 400;

  @Input() showFilterBar = false;

  /** 是否启用多选 */
  @Input() singleSelect = true;

  /** 启用多选后，是否显示checkbox */
  @Input() showCheckbox = true;

  /** 主键字段 */
  @Input() idField: string;

  /** 文本字段 */
  @Input() displayText: string;

  /** 显示图标 */
  @Input() showIcon = false;

  /** 显示边框线 */
  @Input() showBorder = false;

  /** 禁用，禁用时不允许选中行 */
  @Input() disabled = false;

  /**
   * 数据加载方式
   * - all: 全部加载
   * - async: 分层加载
   */
  @Input() loadDataType: 'all' | 'async' = 'all';

  /** 当多选时，选中当前行并钩选 */
  @Input() checkOnSelect = false;

  /** 当多选时，钩选并选中当前行 */
  @Input() selectOnCheck = false;

  /** 启用多选时，此属性为true时，仅当前行选中 */
  @Input() onlySelectSelf = false;

  /** 启用级联选中功能 */
  @Input() cascadeCheck = true;

  /** 向下级联选中 */
  @Input() cascadeDown = true;

  /** 向上级联选中 */
  @Input() cascadeUp = true;

  /** 空数据时，显示的提示文本 */
  @Input() emptyMessage = '暂无数据';

  /** 单击行选中后，在次点击不会被取消选中状态 */
  @Input() keepSelect = true;

  /** 虚拟加载 */
  @Input() virtualized = false;

  /**
   * 仅允许选择叶子节点
   * - default: 应用服务器端传回的设置，如果服务器端没有设置，则与 no 保持一至
   * - yes: 如果客户端设置为 `yes`, 服务器端的设置失效
   * - no: 允许选择所有节点；服务器端设置失效
   */
  @Input() onlySelectLeaf: boolean | 'default' | 'yes' | 'no' = 'default';

  /** 叶子节点图标 */
  @Input() leafIcon = 'file';

  /** 节点展开图标 */
  @Input() expandIcon = 'folder-open';

  /** 节点折叠图标 */
  @Input() collapseIcon = 'folder';

  /** 启用远端排序 */
  @Input() remoteSort = true;

  /** 排序方式 asc | desc */
  @Input() sortOrder: string;

  @Input() data: TreeNode[] = [];

  /** 是否启用双击展开节点 */
  @Input() enableDblClickExpand = false;

  /** 是否启用单击收折节点 */
  @Input() enableClickToggle = false;

  /** 双击时间间隔，通过改时间间隔判断用户是单击还是双击，单位：毫秒 */
  @Input() clickInterval = 400;

  /** 附加按钮点击事件 */
  @Output() appendBtnClick: EventEmitter<any> = new EventEmitter();

  /** 选中事件 */
  @Output() nodeSelected: EventEmitter<any> = new EventEmitter();

  /** 取消选中事件 */
  @Output() nodeUnSelect: EventEmitter<any> = new EventEmitter();

  /** 钩选事件 */
  @Output() nodeChecked: EventEmitter<any> = new EventEmitter();

  /** 取消钩选事件 */
  @Output() nodeUnChecked: EventEmitter<any> = new EventEmitter();

  /** 查询事件 */
  @Output() search: EventEmitter<any> = new EventEmitter();

  @Output() dblClick: EventEmitter<TreeNode> = new EventEmitter();

  @Output() loadedData: EventEmitter<any> = new EventEmitter();

  @Output() expand: EventEmitter<TreeNode> = new EventEmitter();

  @Output() collapse: EventEmitter<TreeNode> = new EventEmitter();

  cols = [];

  constructor(
    public utils: CommonUtils,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cols = [{
      field: 'name',
      title: '',
      visible: true,
      width: 100,
      template: this.treeNodeTemplate
    }];
  }

  ngAfterViewInit(): void {
    this.detectChanges();
  }

  /** 点击事件计时器，处理单击双击事件冲突 */
  private clickTimer = null;
  /** 上一次被单击的节点的id，用于模拟双击事件 */
  private lastClickedNodeId: string = null;

  onNodeClick(row: RowNode) {
    if (this.clickTimer) {
      if (this.lastClickedNodeId && row.node.id === this.lastClickedNodeId) {
        // 两次点击的是同一行数据，触发双击事件
        this.lastClickedNodeId = null;
        this.onDblClick(row && row.node);
        return;
      }
      // 两次点击的是不同行的数据，触发新的单击事件
    }
    this.lastClickedNodeId = row.node.id;
    this.clickTimer = setTimeout(() => {
      this.clickTimer = null;
      this.lastClickedNodeId = null;
      this.handleClickToggle(row);
    }, this.clickInterval);
  }

  private onDblClick(node: TreeNode) {
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
      this.lastClickedNodeId = null;
    }
    this.dblClick.emit(node);
  }

  private handleClickToggle(row: RowNode) {
    if (row && this.enableClickToggle) {
      const node = row.node;
      if (node.leaf) {
        return;
      }
      this.treeTable.toggleExpand(node);
    }
  }

  /**
   * 根据id选中行
   * @todo 联系黄老师排查树表格的bug
   * @param id 数据id
   */
  public selectById(id: string): void {
    if (!id) {
      this.treeTable.clearSelections();
      return;
    }
    this.treeTable.selectNode(id);
  }

  /** 展开直到露出指定的节点 */
  public expandToNodeById(id: string): void {
    const targetRowNode = this.treeTable.findRowNode(id);
    if (!targetRowNode) {
      return;
    }
    let parentNode = targetRowNode.node.parent;
    while (parentNode) {
      if (!parentNode.expanded) {
        this.treeTable.expandNode(parentNode.id, false);
      }
      parentNode = parentNode.parent;
    }
  }

  /**
   * 清空选中状态
   */
  public clearSelection(): void {
    this.treeTable.clearSelections();
  }

  public findNode(id: string): TreeNode {
    const rowNode: RowNode = this.treeTable.findRowNode(id);
    return !!rowNode ? rowNode.node : null;
  }

  public detectChanges(): void {
    this.treeTable.detectChanges();
    this.cd.markForCheck();
    this.cd.detectChanges();
  }

  /** 获取树表格实例 */
  public getTreeTable(): TreeTableComponent {
    return this.treeTable;
  }

  /** 折叠所有节点 */
  public collapseAll(): void {
    this.treeTable && this.treeTable.collapseAll();
  }
  /** 展开所有节点 */
  public expandAll(): void {
    this.treeTable && this.treeTable.expandAll();
  }

}

@NgModule({
  declarations: [TreeComponent, ShowTitleDirective],
  imports: [
    FarrisCommonModule.forRoot(),
    TreeTableModule
  ],
  exports: [TreeComponent, ShowTitleDirective]
})
export class TreeModule { }
